import { useState, useEffect } from 'react';
import {
  DEFAULT_USER_SITE_ID,
  MAX_SUBJECTS_PER_USER,
} from './Contants';
import {
  convertDateToDisplayFormat,
  capitalizeFirstLetter,
  generateSubjectId,
} from './utils';
import { getSitesDropdown, createSubject, getSubjects, getCreatedSubjects } from '../../Config/api';

export const useDataCapture = () => {
  const [subjects, setSubjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [fetchingEnrollmentData, setFetchingEnrollmentData] = useState(false);
  const [user, setUser] = useState(null);
  const [userAssignedSiteId, setUserAssignedSiteId] = useState(DEFAULT_USER_SITE_ID);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [maxSubjectsLimit, setMaxSubjectsLimit] = useState(null);

  const [modal, setModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: null,
    onConfirm: null,
    onCancel: null
  });

  const showModal = (config) => {
    setModal({
      show: true,
      title: config.title || '',
      message: config.message || '',
      type: config.type || 'info',
      confirmText: config.confirmText || 'OK',
      cancelText: config.cancelText || null,
      onConfirm: config.onConfirm || closeModal,
      onCancel: config.onCancel || closeModal
    });
  };

  const closeModal = () => {
    setModal({
      show: false,
      title: '',
      message: '',
      type: 'info',
      confirmText: 'OK',
      cancelText: null,
      onConfirm: null,
      onCancel: null
    });
  };

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      if (parsedUser?.site?.siteId) {
        setUserAssignedSiteId(parsedUser.site.siteId);
        localStorage.setItem('userAssignedSiteId', parsedUser.site.siteId?.toString());
      } else {
        setUserAssignedSiteId(DEFAULT_USER_SITE_ID);
        localStorage.setItem('userAssignedSiteId', DEFAULT_USER_SITE_ID?.toString());
      }
    } else {
      setUserAssignedSiteId(DEFAULT_USER_SITE_ID);
      localStorage.setItem('userAssignedSiteId', DEFAULT_USER_SITE_ID?.toString());
    }
  }, []);

  useEffect(() => {
    const fetchMaxLimit = async () => {
      try {
        const response = await getCreatedSubjects();
        if (response && response.data && response.data.length > 0) {
          const maxSubjects = response.data[0].max_subjects || MAX_SUBJECTS_PER_USER;
          setMaxSubjectsLimit(maxSubjects);
        }
      } catch (error) {
        console.error('Error fetching max subjects limit:', error);
        setMaxSubjectsLimit(MAX_SUBJECTS_PER_USER);
      }
    };

    fetchMaxLimit();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await getSubjects();

      if (response && response.success && response.data) {
        const formattedSubjects = response.data.map((subject) => {
          let siteId = subject.site_id;
          let studyId = subject.study_id;

          if (sites.length > 0) {
            const matchedSite = sites.find(
              s => s.id === subject.site_id
            );

            if (matchedSite) {
              siteId = matchedSite.id;
              studyId = matchedSite.studyId;
            }
          }

          return {
            siteName: subject.site_name,
            siteCode: subject.site_code,
            subjectId: subject.subject_id,
            siteId: siteId,
            subjectNumber: subject.subject_number,
            subjectInitials: subject.subject_initials,
            dateOfEnrollment: subject.enrollment_date,
            screeningNumber: subject.subject_id,
            enrollmentStatus: capitalizeFirstLetter(subject.enrollment_status),
            dataCaptureIcon: true,
            lockStatus: subject.lock_status || 'unlocked',
            progress: subject.progress || 0,
            studyId: subject.study_id,
          };
        });

        setSubjects(formattedSubjects);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleRefreshSubjects = async () => {
    await Promise.all([
      fetchSubjects(),
      fetchSites()
    ]);
  };

  const fetchSites = async () => {
    try {
      setLoadingSites(true);
      const data = await getSitesDropdown();

      if (data.success) {
        const formattedSites = data.data.map((site) => ({
          id: site.site_id,
          code: site.site_code,
          name: site.site_name,
          studyId: site.study_id,
          userId: site.user_id,
          enrolled: 0,
          maxEnrollment: site.max_enrollment || 5,
          progress: 0,
        }));

        setSites(formattedSites);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchEnrollmentDataHandler = async (siteId, currentSubjects = null) => {
    try {
      setFetchingEnrollmentData(true);

      const response = await getCreatedSubjects();

      if (response && response.data && response.data.length > 0) {
        const apiData = response.data[0];
        const maxSubjects = apiData.max_subjects || 10;

        setMaxSubjectsLimit(maxSubjects);

        let subjectsToUse = currentSubjects;

        if (!subjectsToUse || subjectsToUse.length === 0) {
          const freshSubjectsResponse = await getSubjects();
          if (freshSubjectsResponse && freshSubjectsResponse.success && freshSubjectsResponse.data) {
            subjectsToUse = freshSubjectsResponse.data;
          } else {
            subjectsToUse = subjects;
          }
        }

        const enrolledSubjects = subjectsToUse.filter(subject => {
          const subjectSiteId = subject.site_id || subject.siteId;
          return Number(subjectSiteId) === Number(siteId);
        }).length;

        const availableSlots = maxSubjects - enrolledSubjects;

        const enrollmentData = {
          maxSubjects: maxSubjects,
          enrolledSubjects: enrolledSubjects,
          enrollmentStatus: 'screening',
          availableSlots: availableSlots > 0 ? availableSlots : 0
        };

        return enrollmentData;
      }

      return {
        maxSubjects: 10,
        enrolledSubjects: 0,
        enrollmentStatus: 'screening',
        availableSlots: 10
      };
    } catch (error) {
      console.error('Error fetching enrollment data:', error);
      return {
        maxSubjects: 10,
        enrolledSubjects: 0,
        enrollmentStatus: 'screening',
        availableSlots: 10
      };
    } finally {
      setFetchingEnrollmentData(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      fetchSubjects();
    }
  }, [sites.length]);

  useEffect(() => {
    if (sites.length > 0 && subjects.length > 0) {
      const updatedSites = sites.map((site) => ({
        ...site,
        enrolled: subjects.filter((s) => s.siteId === site.id).length,
        progress: Math.round(
          (subjects.filter((s) => s.siteId === site.id).length / site.maxEnrollment) * 100
        ),
      }));
      setSites(updatedSites);
    }
  }, [subjects]);

  const userRoleName = user?.role?.roleName;

  const canWrite = [
    'CRO Administrator',
    'Sponsor',
    'Principal Investigator',
    'Co-ordinator member',
  ].includes(userRoleName);

  const canDataCapture = [
    'Principal Investigator',
    'Co-ordinator member',
  ].includes(userRoleName);

  const userSubjectsCount = subjects.length;
  const userSite = sites.find((s) => s.id === userAssignedSiteId);

  const studyData = {
    totalEnrolments: subjects.length,
    drafts: subjects.filter((s) => s.progress > 0 && s.progress < 100).length,
    completed: subjects.filter((s) => s.progress === 100).length,
  };

  const checkSubjectLimit = () => {
    const limit = maxSubjectsLimit || MAX_SUBJECTS_PER_USER;

    if (userSubjectsCount >= limit) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  const checkSiteEnrollmentLimit = (siteId) => {
    const site = sites.find((s) => s.id === siteId);
    return !(site && site.enrolled >= site.maxEnrollment);
  };

  const addSubject = async (subjectData) => {
    try {
      const selectedSite = sites.find(s => s.id === Number(subjectData.siteId));

      if (!selectedSite) {
        return { success: false, message: 'Selected site not found. Please try again.' };
      }

      const studyId = subjectData.studyId;
      const userId = subjectData.userId;

      if (!studyId) {
        return { success: false, message: 'Study ID not found. Please try again.' };
      }

      if (!userId) {
        return { success: false, message: 'User ID not found. Please try again.' };
      }

      const apiPayload = {
        user_id: userId,
        subject_initials: subjectData.subjectInitials.toUpperCase(),
        study_id: studyId,
        site_id: Number(subjectData.siteId),
        enrollment_date: subjectData.enrollmentDate,
        enrollment_status: subjectData.enrollmentStatus,
      };

      const response = await createSubject(apiPayload);

      if (response && response.success) {
        await Promise.all([
          fetchSubjects(),
          fetchSites()
        ]);

        return {
          success: true,
          message: 'Subject created successfully!',
          data: response.data,
        };
      } else {
        return {
          success: false,
          message: response?.message || 'Failed to create subject. Please try again.'
        };
      }
    } catch (error) {
      console.error('Error adding subject:', error);

      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        'Failed to create subject. Please try again.';

      return { success: false, message: errorMessage };
    }
  };

  const updateSubject = async (subjectData) => {
    try {
      const selectedSite = sites.find(s => s.id === Number(subjectData.siteId));

      if (!selectedSite) {
        return { success: false, message: 'Selected site not found. Please try again.' };
      }

      const studyId = subjectData.studyId;
      const userId = subjectData.userId;

      if (!studyId) {
        return { success: false, message: 'Study ID not found. Please try again.' };
      }

      if (!userId) {
        return { success: false, message: 'User ID not found. Please try again.' };
      }

      if (!subjectData.subjectId) {
        return { success: false, message: 'Subject ID not found. Please try again.' };
      }

      const apiPayload = {
        type: 'edit',
        subject_id: subjectData.subjectId,
        user_id: userId,
        subject_initials: subjectData.subjectInitials.toUpperCase(),
        study_id: studyId,
        site_id: Number(subjectData.siteId),
        enrollment_date: subjectData.enrollmentDate,
        enrollment_status: subjectData.enrollmentStatus,
      };

      const response = await createSubject(apiPayload);

      if (response && response.success) {
        await Promise.all([
          fetchSubjects(),
          fetchSites()
        ]);

        return {
          success: true,
          message: 'Subject updated successfully!',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response?.message || 'Failed to update subject. Please try again.'
        };
      }
    } catch (error) {
      console.error('Error updating subject:', error);

      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        'Failed to update subject. Please try again.';

      return { success: false, message: errorMessage };
    }
  };

  const deleteSubject = (subjectId) => {
    setSubjects((prev) => prev.filter((s) => s.subjectId !== subjectId));
  };

  const toggleSubjectLock = (subjectId) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.subjectId === subjectId
          ? {
            ...s,
            lockStatus: s.lockStatus === 'locked' ? 'unlocked' : 'locked',
          }
          : s
      )
    );
  };

  return {
    subjects,
    sites,
    loadingSites,
    loadingSubjects,
    fetchingEnrollmentData,
    user,
    userRole: userRoleName,
    canWrite,
    canDataCapture,
    userAssignedSiteId,
    userSite,
    userSubjectsCount,
    studyData,
    showLimitModal,
    setShowLimitModal,
    checkSubjectLimit,
    checkSiteEnrollmentLimit,
    addSubject,
    updateSubject,
    deleteSubject,
    toggleSubjectLock,
    generateSubjectId: (siteCode) => generateSubjectId(siteCode, subjects),
    handleRefreshSubjects,
    fetchEnrollmentData: fetchEnrollmentDataHandler,
    maxSubjectsLimit,
    modal,
    showModal,
    closeModal,
  };
};